import { DataChangeEvent } from '@df/core/service/event/data-change-event';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { IDIfference, isDifference } from '@df/util/fn/difference';
import { Nexus } from '@tomandco/nexus-api-types';
import each from 'lodash-es/each';

export interface IBasketItemQtyChange {
    qty: number;
    item: BasketItem;
}

type TChange = 'added' | 'removed';

/**
 * Represents an event that occurs when there is a change in the basket data.
 */
export class BasketDataChangeEvent extends DataChangeEvent<Nexus.Lib.Basket.BasketData> {
    /**
     * Cache for items changes
     */
    private itemsCache = new Map<TChange, IBasketItemQtyChange[]>();

    /**
     * Constructs a new instance of the BasketDataChangeEvent class.
     * @param before The basket data before the change.
     * @param after The basket data after the change.
     * @param basket The basket associated with the event.
     */
    constructor(
        before: Nexus.Lib.Basket.BasketData | undefined,
        after: Nexus.Lib.Basket.BasketData | undefined,
        readonly basket: Basket
    ) {
        super(before, after);
    }

    /**
     * Gets the array of item quantity changes for items that were added.
     * @returns An array of `IItemQtyChange` objects representing the added items, or `undefined` if no items were added.
     */
    get addedItems(): IBasketItemQtyChange[] | undefined {
        return this.getItemChanges('added');
    }

    /**
     * Gets the array of item quantity changes for items that were removed.
     * @returns An array of `IItemQtyChange` objects representing the removed items, or `undefined` if no items were removed.
     */
    get removedItems(): IBasketItemQtyChange[] | undefined {
        return this.getItemChanges('removed');
    }

    /**
     * Retrieves the item quantity changes based on the specified change type.
     * @param type The type of change ('added' or 'removed').
     * @returns An array of `IItemQtyChange` objects representing the item quantity changes, or `undefined` if no changes of the specified type were found.
     */
    private getItemChanges(type: TChange): IBasketItemQtyChange[] | undefined {
        if (!this.itemsCache.has(type)) {
            const addedItems: IBasketItemQtyChange[] = [];
            const removedItems: IBasketItemQtyChange[] = [];

            const delta = this.before && this.after && this.getChange('items').delta;
            const isQuoteEmpty = !this.after?.quote_id;
            if (delta) {
                each(delta, (change: any, itemKey: string) => {
                    const itemChange = change as IDIfference<Nexus.Lib.Basket.BasketItem>;
                    if (isDifference(itemChange)) {
                        const from = itemChange.from;
                        const to = itemChange.to;
                        if (from && !to && !isQuoteEmpty) {
                            removedItems.push({
                                item: this.toItem(from),
                                qty: from.request.qty
                            });
                            return;
                        }
                        if (!from && to) {
                            addedItems.push({
                                item: this.toItem(to),
                                qty: to.request.qty
                            });
                            return;
                        }
                    }

                    const qtyChange = change.request?.qty as IDIfference<number>;
                    if (isDifference(qtyChange)) {
                        const from = qtyChange.from || 0;
                        const to = qtyChange.to || 0;
                        (to > from ? addedItems : removedItems).push({
                            item: this.basket.items![itemKey],
                            qty: Math.abs(to - from)
                        });
                        return;
                    }
                });
            }
            this.itemsCache.set('added', addedItems);
            this.itemsCache.set('removed', removedItems);
        }
        const changes = this.itemsCache.get(type);
        return changes?.length ? changes : undefined;
    }

    /**
     * Converts the specified item data to a basket item.
     * @param itemData The item data to convert.
     * @returns The basket item.
     */
    private toItem(itemData: Nexus.Lib.Basket.BasketItem): BasketItem {
        return this.basket.byId(itemData.id) || this.basket.basketItemFactory.create(itemData, this.basket);
    }
}
